import { SCHEMES } from '@/components/EchartCustomized'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import dayjs from 'dayjs'
import {
    AlertTriangle,
    Calendar,
    FileText,
    Mail,
    TrendingUp,
    Users,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
    CartesianGrid,
    Legend,
    Line,
    LineChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts'
import { Loading, Typogaphy } from 'vnetwork-ui'
import { getSalesActivity } from '../../services'
import { QualifiedProspectsFilters } from '../../types'

interface SalesActivityProps {
    filters: QualifiedProspectsFilters
}

const SalesActivity: React.FC<SalesActivityProps> = ({ filters }) => {
    const { t } = useTranslation()

    const { data, renderState } = useQueryWithHandler(
        {
            queryKey: [
                QUERY_KEYS.REPORTS_SALE_PERFORMANCES,
                'sales-activity',
                filters.from_date,
                filters.to_date,
                filters.inactive_weeks,
            ],
            queryFn: () => getSalesActivity(filters),
            enabled: !!filters.from_date && !!filters.to_date,
        },
        {
            loadingComponent: (
                <div className="flex justify-center items-center h-[400px]">
                    <Loading.CircularProgress width={32} height={32} />
                </div>
            ),
        }
    )

    const stateComponent = renderState()
    if (stateComponent) return stateComponent

    if (!data) {
        return (
            <div className="flex justify-center items-center h-[300px] text-gray-500">
                {t('no_data_available')}
            </div>
        )
    }

    const { note_activity, follower_stats, notes_timeline, at_risk_customers } =
        data

    // Format timeline data for chart
    const timelineData = notes_timeline
        .map((item) => ({
            date: dayjs(item.note_date).format('DD/MM'),
            fullDate: item.note_date,
            notes: item.total_notes,
            customers: item.unique_customers,
            employees: item.unique_employees,
        }))
        .reverse()

    return (
        <div className="space-y-4">
            {/* Notes Timeline Chart */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <TrendingUp className="text-blue-600" size={20} />
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('qualified_prospects.notes_timeline')}
                    </Typogaphy>
                </div>
                {timelineData.length > 0 ? (
                    <ResponsiveContainer width="100%" height={250}>
                        <LineChart
                            data={timelineData}
                            margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                            <CartesianGrid
                                strokeDasharray="3 3"
                                stroke="#e5e7eb"
                            />
                            <XAxis
                                dataKey="date"
                                stroke="#9ca3af"
                                fontSize={11}
                            />
                            <YAxis stroke="#9ca3af" fontSize={11} />
                            <Tooltip
                                labelFormatter={(label, payload) => {
                                    const item = payload?.[0]?.payload
                                    return item
                                        ? dayjs(item.fullDate).format(
                                              'DD/MM/YYYY'
                                          )
                                        : label
                                }}
                                contentStyle={{
                                    backgroundColor: 'white',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '8px',
                                }}
                            />
                            <Legend />
                            <Line
                                type="monotone"
                                dataKey="notes"
                                name={t('qualified_prospects.total_notes')}
                                stroke={SCHEMES.brand[4]}
                                strokeWidth={2}
                                dot={{ fill: SCHEMES.brand[4], r: 4 }}
                                activeDot={{ r: 6 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="customers"
                                name={t('qualified_prospects.unique_customers')}
                                stroke={SCHEMES.success[0]}
                                strokeWidth={2}
                                dot={{ fill: SCHEMES.success[0], r: 4 }}
                            />
                            <Line
                                type="monotone"
                                dataKey="employees"
                                name={t('qualified_prospects.unique_employees')}
                                stroke={SCHEMES.warning[0]}
                                strokeWidth={2}
                                dot={{ fill: SCHEMES.warning[0], r: 4 }}
                            />
                        </LineChart>
                    </ResponsiveContainer>
                ) : (
                    <div className="flex justify-center items-center h-[250px] text-gray-500">
                        {t('no_data_available')}
                    </div>
                )}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Note Activity - Sales Leaderboard */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <FileText className="text-purple-600" size={20} />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('qualified_prospects.note_activity')}
                        </Typogaphy>
                    </div>
                    {note_activity.length > 0 ? (
                        <div className="overflow-auto max-h-[300px]">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {t('employee')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t(
                                                'qualified_prospects.total_notes'
                                            )}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.customers')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.last_note')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {note_activity.map((item, index) => (
                                        <tr
                                            key={item.employee_id}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-3 py-2 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span
                                                        className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                                                            index < 3
                                                                ? 'bg-amber-100 text-amber-800'
                                                                : 'bg-gray-100 text-gray-600'
                                                        }`}
                                                    >
                                                        {index + 1}
                                                    </span>
                                                    <span className="text-sm font-medium text-gray-900">
                                                        {item.employee_name}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-blue-600">
                                                {item.total_notes.toLocaleString()}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-gray-600">
                                                {item.customers_with_notes}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-xs text-right text-gray-500">
                                                {dayjs(
                                                    item.last_note_at
                                                ).format('DD/MM HH:mm')}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-[200px] text-gray-500">
                            {t('no_data_available')}
                        </div>
                    )}
                </div>

                {/* Follower Stats */}
                <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                    <div className="flex items-center gap-2 mb-4">
                        <Users className="text-green-600" size={20} />
                        <Typogaphy className="text-lg font-semibold text-gray-800">
                            {t('qualified_prospects.follower_stats')}
                        </Typogaphy>
                    </div>
                    {follower_stats.length > 0 ? (
                        <div className="overflow-auto max-h-[300px]">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50 sticky top-0">
                                    <tr>
                                        <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">
                                            {t('employee')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.following')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.active')}
                                        </th>
                                        <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">
                                            {t('qualified_prospects.inactive')}
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                    {follower_stats.map((item) => (
                                        <tr
                                            key={item.employee_id}
                                            className="hover:bg-gray-50"
                                        >
                                            <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">
                                                {item.employee_name}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right font-bold text-blue-600">
                                                {item.total_customers_following}
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-green-600">
                                                {
                                                    item.customers_with_recent_activity
                                                }
                                            </td>
                                            <td className="px-3 py-2 whitespace-nowrap text-sm text-right text-red-600">
                                                {item.customers_inactive}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="flex justify-center items-center h-[200px] text-gray-500">
                            {t('no_data_available')}
                        </div>
                    )}
                </div>
            </div>

            {/* At-Risk Customers */}
            <div className="bg-white rounded-lg p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-2 mb-4">
                    <AlertTriangle className="text-red-600" size={20} />
                    <Typogaphy className="text-lg font-semibold text-gray-800">
                        {t('qualified_prospects.at_risk_customers')}
                    </Typogaphy>
                    <span className="bg-red-100 text-red-700 px-2 py-0.5 rounded-full text-xs font-medium">
                        {at_risk_customers.length}
                    </span>
                </div>
                {at_risk_customers.length > 0 ? (
                    <div className="overflow-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('customer')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.industry')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.journey')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.last_note')}
                                    </th>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                                        {t('qualified_prospects.followers')}
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {at_risk_customers.map((customer) => (
                                    <tr
                                        key={customer.customer_id}
                                        className="hover:bg-red-50"
                                    >
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div>
                                                <div className="text-sm font-medium text-gray-900">
                                                    {customer.customer_name}
                                                </div>
                                                <div className="flex items-center gap-1 text-xs text-gray-500">
                                                    <Mail size={12} />
                                                    {customer.customer_email}
                                                </div>
                                                {customer.company_name && (
                                                    <div className="text-xs text-gray-400">
                                                        {customer.company_name}
                                                    </div>
                                                )}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-600">
                                            {customer.customer_industry || '-'}
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex flex-col gap-1">
                                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                                    {t(
                                                        `qualified_prospects.journey_steps.${customer.customer_journey_step}`
                                                    )}
                                                </span>
                                                <span className="text-xs text-gray-500">
                                                    {t(
                                                        `qualified_prospects.statuses.${customer.customer_journey_step_status}`
                                                    )}
                                                </span>
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 whitespace-nowrap">
                                            <div className="flex items-center gap-1 text-xs text-red-600">
                                                <Calendar size={12} />
                                                {dayjs(
                                                    customer.last_note_at
                                                ).format('DD/MM/YYYY')}
                                            </div>
                                            <div className="text-[10px] text-gray-400">
                                                {dayjs(
                                                    customer.last_note_at
                                                ).fromNow()}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3">
                                            <div className="flex flex-wrap gap-1">
                                                {customer.sale_followers.map(
                                                    (follower, idx) => (
                                                        <span
                                                            key={idx}
                                                            className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] bg-gray-100 text-gray-700"
                                                        >
                                                            {follower}
                                                        </span>
                                                    )
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center h-[150px] text-gray-500">
                        <AlertTriangle
                            size={32}
                            className="text-green-500 mb-2"
                        />
                        <Typogaphy>
                            {t('qualified_prospects.no_at_risk_customers')}
                        </Typogaphy>
                    </div>
                )}
            </div>
        </div>
    )
}

export default SalesActivity
